// UI-4: right-click context menu with one-level submenus, keyboard-closable.
import { useEffect, useRef, useState } from "preact/hooks";
import type { MenuItem } from "../tools/toolManager";
import { Icon } from "./icons";

export interface MenuState {
  x: number; // client coords
  y: number;
  items: MenuItem[];
}

export function ContextMenu({ state, onClose }: { state: MenuState; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [openSub, setOpenSub] = useState<number>(-1);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        e.stopPropagation();
        return;
      }
      // roving focus over the visible items (menus are keyboard-navigable, A11y)
      if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
        const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>(".menu-item:not(:disabled)") ?? [])];
        if (items.length === 0) return;
        const current = items.indexOf(document.activeElement as HTMLButtonElement);
        const next =
          e.key === "Home"
            ? 0
            : e.key === "End"
              ? items.length - 1
              : e.key === "ArrowDown"
                ? (current + 1) % items.length
                : (current - 1 + items.length) % items.length;
        items[next]!.focus();
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [onClose]);

  // clamp inside the window
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.right > innerWidth) el.style.left = `${Math.max(0, innerWidth - r.width - 4)}px`;
    if (r.bottom > innerHeight) el.style.top = `${Math.max(0, innerHeight - r.height - 4)}px`;
  }, [state]);

  const run = (item: MenuItem) => {
    if (item.disabled || !item.action) return;
    item.action();
    onClose();
  };

  return (
    <div ref={ref} class="context-menu" role="menu" style={{ left: state.x, top: state.y }}>
      {state.items.map((item, i) => (
        <div key={i}>
          {item.separator && <div class="menu-sep" />}
          <button
            role="menuitem"
            class={`menu-item${item.disabled ? " disabled" : ""}`}
            disabled={item.disabled}
            onClick={() => (item.children ? setOpenSub(openSub === i ? -1 : i) : run(item))}
            onMouseEnter={() => setOpenSub(item.children ? i : -1)}
          >
            <span>{item.label}</span>
            {item.children && (
              <span class="menu-caret">
                <Icon name="caret-right" size={11} />
              </span>
            )}
          </button>
          {item.children && openSub === i && (
            <div class="context-menu submenu" role="menu">
              {item.children.map((sub, j) => (
                <button
                  key={j}
                  role="menuitem"
                  class={`menu-item${sub.disabled ? " disabled" : ""}`}
                  disabled={sub.disabled}
                  onClick={() => run(sub)}
                >
                  {sub.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
